import { useCallback } from "react";
import { Formik, FormikProps } from "formik";
import { Row, Col } from "antd";
import { Form, InputNumber, SubmitButton } from "formik-antd";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Setting as SettingType } from "/app/src/models";
import { settingService } from "/app/src/services";
import { buildParams } from "/app/src/helpers";
import { handlePromiseError } from "/app/src/helpers/api";

/**
 * Component to display input controls for API rate limits
 * - Rate limit per second (default 3)
 * - Rate limit per minute (default 120)
 */
export default function RateLimits() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Per second
  const { data: perSecondSetting } = useQuery({
    queryKey: ["rateLimitPerSecond"],
    queryFn: () => {
      return settingService
        .getAll(buildParams({ number: 114 }))
        .then((response) => response.settings[0]);
    },
    initialData: { value: undefined },
    select: (data: SettingType) => data,
  });

  const { mutateAsync: updatePerSecond } = useMutation({
    mutationFn: (setting: SettingType) => {
      return settingService
        .updateSingle(setting.id, { value: setting.value })
        .then(handlePromiseError);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["rateLimitPerSecond"], data.setting);
    },
  });

  const { mutateAsync: createPerSecond } = useMutation({
    mutationFn: (setting: SettingType) => {
      return settingService
        .createSingle({
          value: setting.value,
          name: "rateLimitPerSecond",
          number: 114,
        })
        .then(handlePromiseError);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["rateLimitPerSecond"], data.setting);
    },
  });

  const onSubmitPerSecond = useCallback(
    async (
      { value }: { value: number | string },
      {
        setFieldError,
      }: { setFieldError: (field: string, value: string) => void },
    ): Promise<void> => {
      if (perSecondSetting?.id) {
        await updatePerSecond({
          id: perSecondSetting.id,
          value: String(value),
        }).catch((error) => setFieldError("value", error.errors[0]));
      } else {
        await createPerSecond({ value: String(value) }).catch((error) =>
          setFieldError("value", error.errors[0]),
        );
      }
    },
    [createPerSecond, updatePerSecond, perSecondSetting?.id],
  );

  const perSecondForm: (
    props: FormikProps<{ value: string | undefined }>,
  ) => JSX.Element = useCallback(
    ({ dirty }) => (
      <Form layout="vertical">
        <Row justify="start" gutter={16}>
          <Col span={8}>
            <h3>{t("translation:rate_limit_per_second")}</h3>
            <p>{t("translation:rate_limit_per_second_description")}</p>
          </Col>
          <Col span={13}>
            <Form.Item className="no-label" name="value">
              <InputNumber name="value" size="large" min={3} max={60} />
            </Form.Item>
          </Col>
          <Col span={3}>
            <SubmitButton
              className="no-label"
              type="primary"
              size="large"
              block
              disabled={!dirty}
            >
              {t("translation:save")}
            </SubmitButton>
          </Col>
        </Row>
      </Form>
    ),
    [t],
  );

  // Per minute
  const { data: perMinuteSetting } = useQuery({
    queryKey: ["rateLimitPerMinute"],
    queryFn: () => {
      return settingService
        .getAll(buildParams({ number: 113 }))
        .then((response) => response.settings[0]);
    },
    initialData: { value: undefined },
    select: (data: SettingType) => data,
  });

  const { mutateAsync: updatePerMinute } = useMutation({
    mutationFn: (setting: SettingType) => {
      return settingService
        .updateSingle(setting.id, { value: setting.value })
        .then(handlePromiseError);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["rateLimitPerMinute"], data.setting);
    },
  });

  const { mutateAsync: createPerMinute } = useMutation({
    mutationFn: (setting: SettingType) => {
      return settingService
        .createSingle({
          value: setting.value,
          name: "rateLimitPerMinute",
          number: 113,
        })
        .then(handlePromiseError);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["rateLimitPerMinute"], data.setting);
    },
  });

  const onSubmitPerMinute = useCallback(
    async (
      { value }: { value: number | string },
      {
        setFieldError,
      }: { setFieldError: (field: string, value: string) => void },
    ): Promise<void> => {
      if (perMinuteSetting?.id) {
        await updatePerMinute({
          id: perMinuteSetting.id,
          value: String(value),
        }).catch((error) => setFieldError("value", error.errors[0]));
      } else {
        await createPerMinute({ value: String(value) }).catch((error) =>
          setFieldError("value", error.errors[0]),
        );
      }
    },
    [createPerMinute, updatePerMinute, perMinuteSetting?.id],
  );

  const perMinuteForm: (
    props: FormikProps<{ value: string | undefined }>,
  ) => JSX.Element = useCallback(
    ({ dirty }) => (
      <Form layout="vertical">
        <Row justify="start" gutter={16}>
          <Col span={8}>
            <h3>{t("translation:rate_limit_per_minute")}</h3>
            <p>{t("translation:rate_limit_per_minute_description")}</p>
          </Col>
          <Col span={13}>
            <Form.Item className="no-label" name="value">
              <InputNumber name="value" size="large" min={120} max={7200} />
            </Form.Item>
          </Col>
          <Col span={3}>
            <SubmitButton
              className="no-label"
              type="primary"
              size="large"
              block
              disabled={!dirty}
            >
              {t("translation:save")}
            </SubmitButton>
          </Col>
        </Row>
      </Form>
    ),
    [t],
  );

  return (
    <>
      <Formik
        component={perSecondForm}
        enableReinitialize
        initialValues={{ value: perSecondSetting?.value ?? 3 }}
        onSubmit={onSubmitPerSecond}
      />
      <Formik
        component={perMinuteForm}
        enableReinitialize
        initialValues={{ value: perMinuteSetting?.value ?? 120 }}
        onSubmit={onSubmitPerMinute}
      />
    </>
  );
}
